import { memo, useMemo, type JSX, type ReactNode } from 'react';
import { markdownTable, type MarkdownAlign } from './markdownTable.js';
import type { RecordLink } from './types.js';

/**
 * Markdown d'un modèle rendu comme noeuds React : aucun HTML du modèle n'est
 * interprété, seuls les liens http(s), mailto:, tel: et les chemins relatifs
 * deviennent des liens. Le sous-ensemble couvre ce que les modèles produisent
 * vraiment : titres, listes imbriquées, cases, citations, code, tableaux.
 */

type Block =
  | { kind: 'p'; lines: string[] }
  | { kind: 'h'; level: 3 | 4; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'hr' }
  | { kind: 'quote'; blocks: Block[] }
  | { kind: 'list'; ordered: boolean; start: number; items: { task?: 'done' | 'todo'; blocks: Block[] }[] }
  | { kind: 'table'; headers: string[]; align: MarkdownAlign[]; rows: string[][] };

const FENCE = /^\s{0,3}(`{3,}|~{3,})\s*([^`\s]*)/;
const HEADING = /^\s{0,3}(#{1,6})\s+(.*?)(?:\s+#+)?\s*$/;
const RULE = /^\s{0,3}([-*_])(?:\s*\1){2,}\s*$/;
const QUOTE = /^\s{0,3}>\s?/;
const ITEM = /^(\s*)([-*+]|\d{1,9}[.)])\s+(.*)$/;
const SETEXT = /^\s{0,3}(?:-{2,}|={2,})\s*$/;
const MAX_DEPTH = 6;

function indentOf(line: string): number {
  return /^\s*/.exec(line)?.[0].length ?? 0;
}

function startsBlock(lines: readonly string[], at: number): boolean {
  const line = lines[at] ?? '';
  return FENCE.test(line) || HEADING.test(line) || RULE.test(line) || QUOTE.test(line) || ITEM.test(line) || markdownTable(lines, at) !== null;
}

function parseList(lines: readonly string[], at: number, depth: number): { block: Block; next: number } {
  const first = ITEM.exec(lines[at] ?? '')!;
  const base = first[1]!.length;
  const ordered = /\d/.test(first[2]!);
  const items: { task?: 'done' | 'todo'; blocks: Block[] }[] = [];
  let i = at;
  while (i < lines.length) {
    const marker = ITEM.exec(lines[i] ?? '');
    if (!marker || marker[1]!.length !== base || /\d/.test(marker[2]!) !== ordered) break;
    const contentIndent = base + marker[2]!.length + 1;
    const body: string[] = [marker[3] ?? ''];
    i++;
    while (i < lines.length) {
      const line = lines[i] ?? '';
      if (!line.trim()) {
        let ahead = i + 1;
        while (ahead < lines.length && !(lines[ahead] ?? '').trim()) ahead++;
        const following = lines[ahead];
        if (following === undefined || indentOf(following) <= base) break;
        body.push('');
        i++;
        continue;
      }
      if (indentOf(line) <= base) break;
      body.push(line.slice(Math.min(indentOf(line), contentIndent)));
      i++;
    }
    const task = /^\[([ xX])\]\s+/.exec(body[0] ?? '');
    if (task) body[0] = (body[0] ?? '').slice(task[0].length);
    items.push({ ...(task ? { task: task[1] === ' ' ? 'todo' as const : 'done' as const } : {}), blocks: parseBlocks(body, depth + 1) });
    let ahead = i;
    while (ahead < lines.length && !(lines[ahead] ?? '').trim()) ahead++;
    const sibling = ITEM.exec(lines[ahead] ?? '');
    if (ahead > i && sibling && sibling[1]!.length === base && /\d/.test(sibling[2]!) === ordered) i = ahead;
  }
  return { block: { kind: 'list', ordered, start: ordered ? Number.parseInt(first[2]!, 10) || 1 : 1, items }, next: i };
}

function parseBlocks(lines: readonly string[], depth = 0): Block[] {
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? '';
    if (!line.trim()) { i++; continue; }
    const fence = FENCE.exec(line);
    if (fence) {
      const marker = fence[1]!;
      const body: string[] = [];
      i++;
      while (i < lines.length && !new RegExp(`^\\s{0,3}${marker[0] === '`' ? '`' : '~'}{${marker.length},}\\s*$`).test(lines[i] ?? '')) body.push(lines[i++] ?? '');
      i++;
      blocks.push({ kind: 'code', text: body.join('\n') });
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      blocks.push({ kind: 'h', level: heading[1]!.length <= 2 ? 3 : 4, text: heading[2] ?? '' });
      i++;
      continue;
    }
    const table = markdownTable(lines, i);
    if (table) {
      blocks.push({ kind: 'table', headers: table.headers, align: table.align, rows: table.rows });
      i = table.next;
      continue;
    }
    if (RULE.test(line)) { blocks.push({ kind: 'hr' }); i++; continue; }
    if (QUOTE.test(line) && depth < MAX_DEPTH) {
      const body: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i] ?? '')) body.push((lines[i++] ?? '').replace(QUOTE, ''));
      blocks.push({ kind: 'quote', blocks: parseBlocks(body, depth + 1) });
      continue;
    }
    if (ITEM.test(line) && depth < MAX_DEPTH) {
      const list = parseList(lines, i, depth);
      blocks.push(list.block);
      i = list.next;
      continue;
    }
    const paragraph = [line.trim()];
    i++;
    let underlined = false;
    while (i < lines.length && (lines[i] ?? '').trim()) {
      if (SETEXT.test(lines[i] ?? '')) { underlined = true; i++; break; }
      if (startsBlock(lines, i)) break;
      paragraph.push((lines[i++] ?? '').trim());
    }
    blocks.push(underlined ? { kind: 'h', level: 3, text: paragraph.join(' ') } : { kind: 'p', lines: paragraph });
  }
  return blocks;
}

function safeHref(url: string): string | null {
  if (/^https?:\/\/[^\s]+$/i.test(url) || /^mailto:[^\s]+$/i.test(url) || /^tel:[+\d][\d\s().-]*$/i.test(url) || /^\/(?![/\\])[^\s\\]*$/.test(url)) return url;
  return null;
}

/** Un chemin de l'app reste dans l'onglet ; tout le reste s'ouvre à côté. */
function external(href: string): { target?: string; rel?: string } {
  return href.startsWith('/') ? {} : { target: '_blank', rel: 'noopener noreferrer' };
}

export function normalizeTitle(value: string): string {
  return value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[*_`~]/g, '').replace(/\s+/g, ' ').trim().toLocaleLowerCase('fr');
}

interface Links {
  find: (text: string) => RecordLink | undefined;
  open?: (record: RecordLink) => void;
}

/** Dans un lien ou un bouton de fiche, plus rien d'interactif : jamais de bouton dans un bouton. */
const INERT: Links = { find: () => undefined };

function RecordButton({ record, links, children }: { record: RecordLink; links: Links; children: ReactNode }): JSX.Element {
  return <button type="button" onClick={() => links.open?.(record)} aria-label={`Ouvrir la fiche ${record.title}`} data-record-link={record.id}
    className="rounded-sm text-left font-semibold text-ink-900 underline decoration-accent-border decoration-2 underline-offset-[3px] hover:decoration-accent-soft-ink focus-visible:shadow-ring">{children}</button>;
}

const INLINE = new RegExp([
  String.raw`\\([\\\x60*_{}\[\]()#+\-.!|~>])`,
  String.raw`(\x60+)([\s\S]+?)\2(?!\x60)`,
  String.raw`\[([^\]\n]{1,200})\]\(\s*<?((?:[^\s()<>]|\([^\s()<>]*\))+)>?(?:\s+"[^"]*")?\s*\)`,
  String.raw`<((?:https?:\/\/|mailto:)[^\s>]+)>`,
  String.raw`(https?:\/\/[^\s<>()]*[^\s<>().,;:!?'"»])`,
  String.raw`\*\*(?=\S)([\s\S]+?)(?<=\S)\*\*`,
  String.raw`(?<![\p{L}\p{N}_])__(?=\S)([\s\S]+?)(?<=\S)__(?![\p{L}\p{N}_])`,
  String.raw`~~(?=\S)([\s\S]+?)(?<=\S)~~`,
  String.raw`(?<![\p{L}\p{N}*\\])\*(?=[^\s*])([^*\n]+?)(?<=[^\s*\\])\*(?![\p{L}\p{N}*])`,
  String.raw`(?<![\p{L}\p{N}_\\])_(?=[^\s_])([^_\n]+?)(?<=[^\s_])_(?![\p{L}\p{N}_])`,
].join('|'), 'gu');

function inline(text: string, links: Links, prefix = 'i'): ReactNode[] {
  const parts: ReactNode[] = [];
  let from = 0;
  for (const match of text.matchAll(INLINE)) {
    const at = match.index ?? 0;
    if (at > from) parts.push(text.slice(from, at));
    const key = `${prefix}-${at}`;
    const [, escaped, , code, label, url, auto, bare, strong, strongAlt, strike, em, emAlt] = match;
    if (escaped !== undefined) parts.push(escaped);
    else if (code !== undefined) parts.push(<code key={key} className="rounded bg-surface-3 px-1 py-px font-mono text-[0.8125em] [overflow-wrap:anywhere]">{code.trim() || code}</code>);
    else if (label !== undefined && url !== undefined) {
      const href = safeHref(url);
      parts.push(href ? <a key={key} href={href} {...external(href)} className="font-medium text-ink-900 underline underline-offset-2">{inline(label, INERT, key)}</a>
        : <span key={key}>{inline(label, links, key)}</span>);
    } else if (auto !== undefined || bare !== undefined) {
      const href = safeHref(auto ?? bare!);
      parts.push(href ? <a key={key} href={href} {...external(href)} className="font-medium text-ink-900 underline underline-offset-2 [overflow-wrap:anywhere]">{(auto ?? bare!).replace(/^mailto:/i, '')}</a> : (auto ?? bare!));
    } else if (strong !== undefined || strongAlt !== undefined) {
      const content = strong ?? strongAlt!;
      const record = links.find(content);
      parts.push(record ? <RecordButton key={key} record={record} links={links}>{inline(content, INERT, key)}</RecordButton>
        : <strong key={key} className="font-semibold">{inline(content, links, key)}</strong>);
    } else if (strike !== undefined) parts.push(<del key={key}>{inline(strike, links, key)}</del>);
    else if (em !== undefined || emAlt !== undefined) parts.push(<em key={key}>{inline(em ?? emAlt!, links, key)}</em>);
    from = at + match[0].length;
  }
  if (from < text.length) parts.push(text.slice(from));
  return parts;
}

const NUMERIC = /^[+\-−]?\s?[€$£]?\s?\d[\d\s\u00a0\u202f.,’']*\s?(?:%|€|\$|£|CHF|EUR|k€|M€|pts?)?$|^\d+\s?h(?:\s?\d{1,2}(?:\s?min)?)?$|^\d+\s?min$/i;
const DATE = /^\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}$|^\d{4}-\d{2}-\d{2}$/;

function plain(text: string): string {
  return text.replace(/\*\*|__|~~|`/g, '').trim();
}

function Cell({ as: Tag, text, align, numeric, first, links }: { as: 'th' | 'td'; text: string; align: MarkdownAlign; numeric: boolean; first: boolean; links: Links }): JSX.Element {
  const record = Tag === 'td' ? links.find(text) : undefined;
  const date = DATE.test(plain(text));
  return <Tag scope={Tag === 'th' ? 'col' : undefined} data-align={align ?? undefined} data-numeric={numeric ? '' : undefined}
    className={[
      'px-2.5 py-1.5 align-top',
      Tag === 'th' ? 'whitespace-nowrap text-[0.6875rem] font-semibold text-ink-500' : 'text-ink-900',
      numeric || date ? 'whitespace-nowrap tabular-nums' : 'min-w-[6rem] max-w-[15rem] break-words',
      align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left',
      first ? 'sticky left-0 z-[1] bg-accent-tint' : '',
    ].join(' ')}>
    {record ? <RecordButton record={record} links={links}>{inline(text.replace(/^\*\*(.*)\*\*$/, '$1'), INERT)}</RecordButton> : inline(text, links)}
  </Tag>;
}

function Table({ block, links }: { block: Extract<Block, { kind: 'table' }>; links: Links }): JSX.Element {
  const numericColumn = block.headers.map((_, column) => {
    const values = block.rows.map((row) => plain(row[column] ?? '')).filter(Boolean);
    return values.length > 0 && values.every((value) => NUMERIC.test(value));
  });
  const alignOf = (column: number): MarkdownAlign => block.align[column] ?? (numericColumn[column] ? 'right' : null);
  return <div data-md-table="" tabIndex={0} role="region" aria-label="Tableau"
    className="max-w-full overflow-x-auto overscroll-x-contain rounded-md bg-accent-tint focus-visible:shadow-ring">
    <table className="w-full border-collapse text-left text-[0.8125rem] leading-4">
      <thead><tr className="border-b border-accent-tint-border">{block.headers.map((cell, column) =>
        <Cell key={column} as="th" text={cell} align={alignOf(column)} numeric={false} first={column === 0 && block.headers.length > 2} links={links} />)}</tr></thead>
      <tbody>{block.rows.map((row, index) => <tr key={index} className="border-b border-accent-tint-border last:border-b-0">
        {row.map((cell, column) => <Cell key={column} as="td" text={cell} align={alignOf(column)}
          numeric={NUMERIC.test(plain(cell)) && plain(cell) !== ''} first={column === 0 && block.headers.length > 2} links={links} />)}
      </tr>)}</tbody>
    </table>
  </div>;
}

function paragraph(lines: readonly string[], links: Links, prefix: string): ReactNode[] {
  return lines.flatMap((line, index) => index === 0 ? inline(line, links, `${prefix}-${index}`)
    : [<br key={`${prefix}-br-${index}`} />, ...inline(line, links, `${prefix}-${index}`)]);
}

function Blocks({ blocks, links, tight = false, prefix = 'b' }: { blocks: Block[]; links: Links; tight?: boolean; prefix?: string }): JSX.Element {
  return <>{blocks.map((block, index) => {
    const key = `${prefix}-${index}`;
    switch (block.kind) {
      case 'p': return tight && blocks.length === 1 ? <span key={key}>{paragraph(block.lines, links, key)}</span>
        : <p key={key}>{paragraph(block.lines, links, key)}</p>;
      case 'h': return block.level === 3
        ? <h3 key={key} className="pt-1 text-[0.9375rem] font-semibold leading-5 text-ink-900">{inline(block.text, links, key)}</h3>
        : <h4 key={key} className="pt-0.5 text-sm font-semibold leading-5 text-ink-900">{inline(block.text, links, key)}</h4>;
      case 'code': return <pre key={key} className="max-w-full whitespace-pre-wrap rounded-md bg-surface-3 px-3 py-2 font-mono text-xs leading-5 text-ink-900 [overflow-wrap:anywhere]"><code>{block.text}</code></pre>;
      case 'hr': return <hr key={key} className="border-border" />;
      case 'quote': return <blockquote key={key} className="flex flex-col gap-2 border-l-2 border-accent-border pl-3 text-ink-700"><Blocks blocks={block.blocks} links={links} prefix={key} /></blockquote>;
      case 'table': return <Table key={key} block={block} links={links} />;
      case 'list': {
        const items = block.items.map((item, itemIndex) => <li key={`${key}-${itemIndex}`} data-task={item.task}
          className={item.task ? 'flex list-none items-start gap-2 [&>div]:min-w-0' : 'pl-0.5'}>
          {item.task && <span aria-hidden="true" className={['mt-[5px] flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[4px] border',
            item.task === 'done' ? 'border-accent-soft-ink bg-accent-soft-ink' : 'border-border-strong'].join(' ')}>
            {item.task === 'done' && <span className="mb-px h-1.5 w-2.5 -rotate-45 border-b-2 border-l-2 border-surface" />}</span>}
          {item.task && <span className="sr-only">{item.task === 'done' ? 'Fait : ' : 'À faire : '}</span>}
          <div className="flex min-w-0 flex-col gap-1"><Blocks blocks={item.blocks} links={links} tight prefix={`${key}-${itemIndex}`} /></div>
        </li>);
        return block.ordered
          ? <ol key={key} start={block.start === 1 ? undefined : block.start} className="flex list-decimal flex-col gap-0.5 pl-4 marker:text-ink-500">{items}</ol>
          : <ul key={key} className={['flex flex-col gap-0.5', block.items.every((item) => item.task) ? 'pl-0' : 'list-disc pl-4 marker:text-ink-400'].join(' ')}>{items}</ul>;
      }
    }
  })}</>;
}

export interface MessageTextProps {
  text: string;
  /** Fiches connues du tour : une ligne de tableau ou un nom en gras qui les cite devient ouvrable. */
  records?: readonly RecordLink[];
  onOpenRecord?: (record: RecordLink) => void;
}

function MessageTextView({ text, records = [], onOpenRecord }: MessageTextProps): JSX.Element {
  const links = useMemo<Links>(() => {
    const byTitle = new Map<string, RecordLink | null>();
    if (onOpenRecord) for (const record of records) {
      const title = normalizeTitle(record.title);
      if (!title) continue;
      const known = byTitle.get(title);
      byTitle.set(title, known === undefined || known?.id === record.id ? record : null);
    }
    return { find: (value) => byTitle.get(normalizeTitle(value)) ?? undefined, open: onOpenRecord };
  }, [records, onOpenRecord]);
  const blocks = useMemo(() => parseBlocks(text.replace(/\r\n?/g, '\n').replace(/\t/g, '    ').split('\n')), [text]);
  return <div className="flex min-w-0 flex-col gap-2 break-words [overflow-wrap:anywhere] [&_table]:[overflow-wrap:normal]">
    <Blocks blocks={blocks} links={links} />
  </div>;
}

const recordKey = (records: readonly RecordLink[] = []): string => records.map((record) => `${record.id}:${record.title}`).join('|');

/** Mémoïsé : redimensionner le panneau ou recevoir un fragment ne re-analyse pas tout le fil. */
export const MessageText = memo(MessageTextView, (before, after) => before.text === after.text &&
  before.onOpenRecord === after.onOpenRecord && recordKey(before.records) === recordKey(after.records));
